import { BRAND_PERSON_NAME, BRAND_SOURCE_KEY } from "@/lib/brandConfig";
/**
 * Checkout AR Channel Invite — Stripe-only (MercadoPago retirado).
 *
 * Al montar, crea una Checkout Session de suscripción basic y redirige a Stripe.
 * Success → /portal/complete-profile (rewrite API por plan_tier).
 */

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { buildStripeSuccessReturnUrl } from "@/lib/muchasGraciasSuccessUrl";
import { getDataFastStripeMetadata } from "@/lib/datafast";
import { AlertCircle, Loader2 } from "lucide-react";

const PRODUCT_NAME = `Canal Exclusivo ${BRAND_PERSON_NAME}`;

export default function CheckoutArChannelInvite() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  // Prefer `code` for invite tokens — `ref` is also a DataFast campaign param
  // (https://datafa.st/docs/utm-tracking). Using code avoids polluting Campaign tab.
  const invitationCode = searchParams.get("code") || searchParams.get("ref") || undefined;

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const base = window.location.origin;
        const amountCents = Math.round(
          parseFloat(String(import.meta.env.VITE_PREMIUM_USD_STANDARD_PRICE ?? "29.99")) * 100,
        );
        const stripeResp = await fetch(`${base}/api/create-subscription-checkout`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            amount: amountCents,
            currency: "usd",
            productName: PRODUCT_NAME,
            country: "AR",
            successUrl: buildStripeSuccessReturnUrl(base),
            cancelUrl: `${base}/checkout-ar-channel-invite`,
            ...getDataFastStripeMetadata(),
            metadata: {
              type: "membresia",
              source: BRAND_SOURCE_KEY,
              flow: "channel_invite_ar_stripe",
              plan_tier: "basic",
              invitation_code: invitationCode || undefined,
              ...getDataFastStripeMetadata(),
            },
          }),
        });
        const stripeData = (await stripeResp.json()) as {
          url?: string;
          error?: string;
          message?: string;
        };
        if (cancelled) return;
        if (stripeResp.ok && stripeData.url) {
          window.location.href = stripeData.url;
          return;
        }
        throw new Error(stripeData.message || stripeData.error || "stripe_subscription_unavailable");
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "No se pudo iniciar el checkout");
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [invitationCode]);

  if (error) {
    return (
      <div className="w-full h-dvh flex flex-col items-center justify-center bg-[#0A0E27] text-white p-6 font-body">
        <div className="max-w-md w-full space-y-4">
          <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-white mb-2 font-display">Error al cargar el checkout</h2>
            <p className="text-red-200 text-sm mb-4 font-medium">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="w-full py-3 px-4 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl font-bold text-white hover:opacity-90 transition-opacity"
            >
              Reintentar
            </button>
          </div>
          <button
            onClick={() => navigate("/canal")}
            className="w-full py-3 text-white/60 hover:text-white text-sm font-semibold transition-colors"
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-dvh flex flex-col items-center justify-center bg-[#0A0E27] text-white p-6">
      <Loader2 className="w-10 h-10 animate-spin text-amber-400 mb-4" />
      <p className="text-sm text-white/70">Abriendo checkout seguro (Stripe)…</p>
    </div>
  );
}
